'use client';

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useTranslations } from 'next-intl';
import { cn } from '@/lib/utils';

export type DialogSize = 'sm' | 'md' | 'lg' | 'xl' | 'full';

const SIZE_CLASSES: Record<DialogSize, string> = {
  sm: 'max-w-sm',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
  full: 'max-w-[calc(100vw-2rem)]',
};

const FOCUSABLE =
  'a[href],button:not([disabled]),input:not([disabled]):not([type="hidden"]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

// Skips elements a browser would not focus. The style checks come first because happy-dom
// reports one client rect for every element, even display:none ones.
function isRendered(el: HTMLElement): boolean {
  if (el.hasAttribute('hidden')) return false;
  const style = getComputedStyle(el);
  if (style.display === 'none' || style.visibility === 'hidden') return false;
  return el.getClientRects().length > 0;
}

// The initial-focus rule: data-autofocus, else the first rendered field that is not the close
// button, else the panel itself.
function initialFocusTarget(panel: HTMLElement): HTMLElement {
  const preferred = panel.querySelector<HTMLElement>('[data-autofocus]');
  const firstField = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE)).find(
    (el) => !el.hasAttribute('data-dialog-close') && isRendered(el),
  );
  return preferred ?? firstField ?? panel;
}

function isNoFocus(el: Element | null): boolean {
  return el === null || el === document.body || el === document.documentElement;
}

// Safari does not focus a button on click, so when a dialog opens from one, activeElement is
// <body> or a page field that kept focus, and the opener would be wrong. A capture-phase
// pointerdown listener, installed once when this module loads (before any `{open && <Dialog/>}`
// can mount), remembers the last pressed focusable control; any keydown forgets it, so a dialog
// opened from the keyboard still uses activeElement.
const PRESS_MEMORY_MS = 1000;
let lastPressed: { el: HTMLElement; at: number } | null = null;

if (typeof document !== 'undefined') {
  document.addEventListener(
    'pointerdown',
    (event) => {
      const target = event.target instanceof Element ? event.target.closest(FOCUSABLE) : null;
      lastPressed = target instanceof HTMLElement ? { el: target, at: Date.now() } : null;
    },
    true,
  );
  document.addEventListener(
    'keydown',
    () => {
      lastPressed = null;
    },
    true,
  );
}

function captureOpener(): HTMLElement | null {
  if (typeof document === 'undefined') return null;
  const active = document.activeElement;
  const pressed =
    lastPressed &&
    Date.now() - lastPressed.at <= PRESS_MEMORY_MS &&
    document.contains(lastPressed.el)
      ? lastPressed.el
      : null;
  // A fresh press wins unless focus already sits on the pressed control or inside it.
  if (pressed && (isNoFocus(active) || !pressed.contains(active))) return pressed;
  return active instanceof HTMLElement ? active : null;
}

interface DialogContextType {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  busy: boolean;
  registerTitle: (id: string) => () => void;
}

const DialogContext = createContext<DialogContextType | undefined>(undefined);

function useDialogContext() {
  const context = useContext(DialogContext);
  if (!context) {
    throw new Error('Dialog components must be used within a Dialog');
  }
  return context;
}

// Open dialogs, topmost last. Escape and the focus trap act only on the top entry, so a
// ConfirmDialog opened over an editor closes alone and the editor keeps its state.
// Entries carry the panel so a parent that mounts in the same commit as its nested child (child
// effects run first) still sorts beneath it.
const openStack: { id: string; panel: HTMLElement | null }[] = [];

// Body scroll is locked once for the whole stack: saved when it goes 0->1, restored on 1->0, so
// independent dialogs closing in one commit cannot leave a stale 'hidden' behind.
let savedBodyOverflow = '';

// Each open panel's focus-restore chain: its opener, then the chain of the dialog that opener sat
// in. A dialog opened from a button inside another dialog that closes in the same click (a "Next"
// handover) falls back along this chain when that button has left the document. Captured at open
// time because the earlier panel is detached by the time the later dialog closes.
const restoreChains = new WeakMap<Element, HTMLElement[]>();

function restoreChainFor(opener: HTMLElement | null): HTMLElement[] {
  // Nothing focused at open reads as <body>; it is not a restore target and would end the search.
  if (!opener || opener === document.body || opener === document.documentElement) return [];
  const host = opener.closest('[role="dialog"]');
  return [opener, ...((host && restoreChains.get(host)) ?? [])];
}

interface DialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: ReactNode;
  /** Panel width. Default 'md' (max-w-lg) matches the pre-2026-10 behaviour. */
  size?: DialogSize;
  /** While true, Escape, backdrop and the close button do nothing (a request is in flight). */
  busy?: boolean;
  /** Accessible name when the dialog has no DialogTitle. */
  'aria-label'?: string;
  /** 'bottom' docks the panel to the bottom edge and slides it up (a sheet). Default 'center'. */
  placement?: 'center' | 'bottom';
}

export function Dialog({
  open,
  onOpenChange,
  children,
  size = 'md',
  busy = false,
  'aria-label': ariaLabel,
  placement = 'center',
}: DialogProps) {
  // Every mounted DialogTitle registers its own id, so removing one of two titles keeps the name.
  const [titleIds, setTitleIds] = useState<string[]>([]);
  const registerTitle = useCallback((id: string) => {
    setTitleIds((ids) => [...ids, id]);
    return () => setTitleIds((ids) => ids.filter((other) => other !== id));
  }, []);
  const instanceId = useId();
  const panelRef = useRef<HTMLDivElement>(null);
  // The element focused inside the panel when the open effect last ran. StrictMode's simulated
  // unmount refocuses the opener before re-running the effect; this lets the re-run put focus back
  // on the autoFocus child instead of the first field. Stale entries sit in an old panel and fail
  // the contains() check.
  const focusedInsideRef = useRef<HTMLElement | null>(null);
  const busyRef = useRef(busy);
  const onOpenChangeRef = useRef(onOpenChange);
  const [wasOpen, setWasOpen] = useState(false);
  const [opener, setOpener] = useState<HTMLElement | null>(null);

  // React applies `autoFocus` during commit, before any effect runs, so the opener has to be read
  // while rendering (once per opening, via the adjust-state-in-render pattern) or the restore
  // target would be an input inside the panel.
  if (open !== wasOpen) {
    setWasOpen(open);
    setOpener(open ? captureOpener() : null);
  }

  useEffect(() => {
    busyRef.current = busy;
    onOpenChangeRef.current = onOpenChange;
  });

  useEffect(() => {
    if (!open) return;
    const ownPanel = panelRef.current;
    const nestedIndex = openStack.findIndex(
      (other) =>
        ownPanel && other.panel && ownPanel !== other.panel && ownPanel.contains(other.panel),
    );
    const entry = { id: instanceId, panel: ownPanel };
    if (nestedIndex === -1) openStack.push(entry);
    else openStack.splice(nestedIndex, 0, entry);
    if (openStack.length === 1) savedBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const panel = panelRef.current;
    const restoreChain = restoreChainFor(opener);
    if (panel) restoreChains.set(panel, restoreChain);
    if (panel && !panel.contains(document.activeElement)) {
      const recorded = focusedInsideRef.current;
      const previous =
        recorded && panel.contains(recorded) && isRendered(recorded) ? recorded : null;
      (previous ?? initialFocusTarget(panel)).focus();
    }
    if (panel && document.activeElement instanceof HTMLElement) {
      focusedInsideRef.current = panel.contains(document.activeElement)
        ? document.activeElement
        : null;
    }

    function onKeyDown(event: KeyboardEvent) {
      if (openStack[openStack.length - 1]?.id !== instanceId) return;
      if (event.key === 'Escape') {
        if (event.defaultPrevented) return;
        // IME composition uses Escape to cancel the candidate, not to dismiss the dialog.
        if (event.isComposing || event.keyCode === 229) return;
        if (busyRef.current) return;
        event.preventDefault();
        onOpenChangeRef.current(false);
        return;
      }
      const current = panelRef.current;
      if (event.key !== 'Tab' || !current) return;
      if (event.defaultPrevented) return;
      const items = Array.from(current.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(isRendered);
      if (items.length === 0) {
        event.preventDefault();
        current.focus();
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement;
      if (!current.contains(active)) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
        return;
      }
      if (event.shiftKey && (active === first || active === current)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    }

    // Removing the focused node (a field swapped for a spinner) drops focus to <body> in every
    // browser, and not all of them fire focusout for it. Any subtree mutation re-checks: only when
    // focus is nowhere (never when it sits in a portal or toast outside the panel) and this
    // dialog is on top does focus go back in by the initial-focus rule.
    const observer =
      panel && typeof MutationObserver !== 'undefined'
        ? new MutationObserver(() => {
            if (openStack[openStack.length - 1]?.id !== instanceId) return;
            if (!isNoFocus(document.activeElement) || !document.contains(panel)) return;
            initialFocusTarget(panel).focus();
          })
        : null;
    if (panel) observer?.observe(panel, { childList: true, subtree: true });

    document.addEventListener('keydown', onKeyDown);
    return () => {
      observer?.disconnect();
      document.removeEventListener('keydown', onKeyDown);
      const index = openStack.indexOf(entry);
      if (index !== -1) openStack.splice(index, 1);
      if (openStack.length === 0) document.body.style.overflow = savedBodyOverflow;
      // While another modal stays open, focus must not land behind it: only chain elements inside
      // the topmost remaining panel qualify, else that panel itself takes focus.
      const below = openStack[openStack.length - 1]?.panel;
      const topPanel = below && document.contains(below) ? below : null;
      if (topPanel?.contains(document.activeElement)) return;
      const target = restoreChain.find(
        (el) => document.contains(el) && isRendered(el) && (!topPanel || topPanel.contains(el)),
      );
      (target ?? topPanel)?.focus();
    };
  }, [open, instanceId, opener]);

  if (!open) return null;

  return (
    <DialogContext.Provider value={{ open, onOpenChange, busy, registerTitle }}>
      <div
        className={cn(
          'fixed inset-0 z-50 flex justify-center',
          placement === 'bottom' ? 'items-end' : 'items-center',
        )}
      >
        <div
          className="fixed inset-0 bg-black/50"
          aria-hidden="true"
          data-testid="dialog-backdrop"
          onClick={() => {
            if (!busy) onOpenChange(false);
          }}
        />
        <div
          ref={panelRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleIds.length > 0 ? titleIds.join(' ') : undefined}
          aria-label={ariaLabel}
          aria-busy={busy || undefined}
          tabIndex={-1}
          className={cn(
            'relative z-10 w-full outline-none',
            placement === 'bottom' ? 'sheet-enter' : 'mx-4',
            SIZE_CLASSES[size],
          )}
        >
          {children}
        </div>
      </div>
    </DialogContext.Provider>
  );
}

interface DialogContentProps {
  children: ReactNode;
  className?: string;
}

export function DialogContent({ children, className = '' }: DialogContentProps) {
  return (
    <div
      className={`bg-card text-card-foreground border-border rounded-lg border shadow-xl ${className}`}
    >
      {children}
    </div>
  );
}

interface DialogHeaderProps {
  children: ReactNode;
}

export function DialogHeader({ children }: DialogHeaderProps) {
  const { onOpenChange, busy } = useDialogContext();
  const t = useTranslations('Common');

  return (
    <div className="border-border flex items-center justify-between border-b p-6">
      <div className="flex-1">{children}</div>
      <button
        type="button"
        data-dialog-close=""
        aria-label={t('buttons.close')}
        disabled={busy}
        onClick={() => onOpenChange(false)}
        className="text-muted hover:bg-surface ml-4 rounded-md p-1 transition-colors disabled:opacity-50"
      >
        <XMarkIcon className="h-5 w-5" aria-hidden="true" />
      </button>
    </div>
  );
}

interface DialogTitleProps {
  children: ReactNode;
}

export function DialogTitle({ children }: DialogTitleProps) {
  const id = useId();
  const registerTitle = useContext(DialogContext)?.registerTitle;
  useLayoutEffect(() => registerTitle?.(id), [registerTitle, id]);
  return (
    <h2 id={id} className="text-foreground text-lg font-semibold">
      {children}
    </h2>
  );
}

interface DialogFooterProps {
  children: ReactNode;
  className?: string;
}

export function DialogFooter({ children, className }: DialogFooterProps) {
  return (
    <div
      className={cn(
        'border-border flex flex-col-reverse gap-2 border-t p-6 sm:flex-row sm:justify-end',
        className,
      )}
    >
      {children}
    </div>
  );
}
